import { Slot } from "radix-ui";
import type * as React from "react";

import { cn } from "../lib/cn";

/**
 * A monospaced label chip: a flow name, a count, an identifier.
 *
 * Deliberately not a Badge. Badge is a pill that carries a signal - status,
 * severity, a verdict you are meant to read colour from. A Tag carries a value,
 * so it stays square-cornered, monospaced and achromatic, and reads as data
 * rather than as a state. Keeping them separate is what stops "chip" collapsing
 * into one component that means both.
 *
 * `asChild` renders the chip as whatever it wraps, so a Tag that navigates can
 * be a real link rather than a span with a click handler.
 */
function Tag({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="tag"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-[5px] border border-border bg-card px-[7px] py-[2px] font-mono text-[10.5px] text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export { Tag };
